import type { CategoryKind, CategoryNature } from './model';

/**
 * The default category tree seeded into every new budget (brief §9).
 * Keys are stable identifiers; display names come from the locale files
 * (categories.<key>). colorSlot is the category's line color and its
 * position in the validated palette order (see DESIGN.md, Slot Order Rule).
 *
 * Decision 2026-09-28: the mortgage lives only under Financial Obligations
 * ("Mortgage Loan"); the brief's Housing > Mortgage was dropped so one
 * installment can never be counted in two places.
 */
export interface DefaultSubcategory {
  key: string;
  unit?: string;
}

export interface DefaultCategory {
  key: string;
  kind: CategoryKind;
  nature: CategoryNature | null;
  colorSlot: number;
  subcategories: DefaultSubcategory[];
}

export const DEFAULT_CATEGORIES: readonly DefaultCategory[] = [
  {
    key: 'housing',
    kind: 'spending',
    nature: 'fixed',
    colorSlot: 1,
    subcategories: [{ key: 'rent' }, { key: 'home_maintenance' }, { key: 'property_costs' }],
  },
  {
    key: 'obligations',
    kind: 'obligation',
    nature: 'fixed',
    colorSlot: 2,
    subcategories: [
      { key: 'personal_loan' },
      { key: 'car_loan' },
      { key: 'mortgage_loan' },
      { key: 'student_loan' },
      { key: 'credit_card_bill' },
      { key: 'installment' },
      { key: 'bank_fee' },
      { key: 'overdraft' },
      { key: 'other_debt' },
    ],
  },
  {
    key: 'food',
    kind: 'spending',
    nature: 'variable',
    colorSlot: 3,
    subcategories: [{ key: 'groceries' }, { key: 'restaurants' }, { key: 'delivery' }, { key: 'coffee' }, { key: 'food_other' }],
  },
  {
    key: 'utilities',
    kind: 'spending',
    nature: 'variable',
    colorSlot: 4,
    subcategories: [
      { key: 'electricity', unit: 'kWh' },
      { key: 'natural_gas', unit: 'm³' },
      { key: 'water', unit: 'm³' },
      { key: 'internet', unit: 'GB' },
      { key: 'mobile', unit: 'GB' },
      { key: 'utility_other' },
    ],
  },
  {
    key: 'shopping',
    kind: 'spending',
    nature: 'discretionary',
    colorSlot: 5,
    subcategories: [{ key: 'clothing' }, { key: 'electronics' }, { key: 'shopping_home' }, { key: 'shopping_personal' }, { key: 'shopping_other' }],
  },
  {
    key: 'transportation',
    kind: 'spending',
    nature: 'variable',
    colorSlot: 6,
    subcategories: [
      { key: 'fuel', unit: 'L' },
      { key: 'public_transport' },
      { key: 'taxi_rideshare' },
      { key: 'parking' },
      { key: 'vehicle_maintenance' },
    ],
  },
  {
    key: 'entertainment',
    kind: 'spending',
    nature: 'discretionary',
    colorSlot: 7,
    subcategories: [
      { key: 'netflix' },
      { key: 'prime_video' },
      { key: 'spotify' },
      { key: 'hbo_max' },
      { key: 'youtube' },
      { key: 'gaming' },
      { key: 'cinema' },
      { key: 'entertainment_other' },
    ],
  },
  { key: 'other', kind: 'spending', nature: null, colorSlot: 0, subcategories: [] },
];
